import cashBackBgDark from '@/assets/images/cashBackBgDark.png';
import cashBackBgLight from '@/assets/images/cashBackBgLight.png';
import { useRootSelector } from '@/hooks/selector.hook';
import { useTheme } from 'next-themes';

export const CashbackCard = () => {
    const dataCashback = useRootSelector((state) => state.cashback.dataCashback);
    const { resolvedTheme } = useTheme();

    // Extract data with fallbacks
    const availableBalance = dataCashback?.metrics?.availableBalance ?? 0;
    const pendingBalance = dataCashback?.metrics?.pendingBalance ?? 0;
    const currentTier = dataCashback?.metrics?.currentTier;
    const nextVestingDate = dataCashback?.metrics?.nextVestingDate;
    const recentAccruals = dataCashback?.recentAccruals ?? [];

    // Find next vesting accrual (first non-vested accrual)
    const nextVestingAccrual = recentAccruals.find(
        (accrual) => accrual.status !== 'Vested' && accrual.cashbackEarned > 0
    );

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
        }).format(amount).replace('$', '');
    };

    // Format date
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Calculate tier range
    const getTierRange = () => {
        if (!currentTier) return '0%-0%';
        const rate = currentTier.rate;

        // Find current tier in config to get the range
        const tiers = dataCashback?.config?.tiers ?? [];
        const currentTierIndex = tiers.findIndex((t) => t.tierName === currentTier.tierName);

        if (currentTierIndex === -1) return `${rate}%`;

        // Get min and max rates from current and next tier
        const currentRate = tiers[currentTierIndex].rate;
        const nextRate = currentTierIndex < tiers.length - 1
            ? tiers[currentTierIndex + 1].rate
            : currentRate;

        if (currentRate === nextRate) {
            return `${currentRate}%`;
        }

        return `${currentRate}%-${nextRate}%`;
    };

    return (
        <div className='bg-[linear-gradient(89.53deg,_#57E8BA_76.92%,_#71FDD1_81.35%,_#318268_100.93%)] pt-[1.4rem] rounded-t-[1.4rem] rounded-b-[1.2rem]'>
            <div className='px-[3.5rem] flex items-center gap-[0.6rem]'>
                <svg width="68" height="10" viewBox="0 0 68 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 5.99426C1.66124 5.99426 3.00953 7.33979 3.00953 8.99761H0L0 5.99426Z" fill="#161616" />
                    <path d="M9.00876 2.99675V8.99025H6.00584V2.99675H0L0 0L6.00584 0C7.66456 0 9.00876 1.34143 9.00876 2.99675Z" fill="#161616" />
                    <path d="M13.5497 6.53154C13.2316 6.17473 13.0276 5.7287 12.9383 5.19267H17.4758C18.35 5.19267 18.9891 5.0006 19.3936 4.61646C19.7981 4.23232 20 3.64968 20 2.86854C20 2.02552 19.7761 1.34724 19.3277 0.832106C18.8793 0.317774 18.2239 0.0606079 17.3604 0.0606079H15.262C14.6605 0.0606079 14.098 0.1691 13.5738 0.386084C13.0495 0.603067 12.5954 0.910059 12.2128 1.30625C11.8303 1.70325 11.5327 2.18062 11.3195 2.73835C11.1063 3.29608 11 3.898 11 4.54172C11 5.18544 11.1063 5.79058 11.3195 6.35474C11.5327 6.9189 11.8303 7.40269 12.2128 7.80532C12.5954 8.20794 13.0488 8.51815 13.5738 8.73513C14.0987 8.95212 14.6612 9.06061 15.262 9.06061H19.5735V7.27572H15.2457C14.5571 7.27572 13.9917 7.02819 13.549 6.53154H13.5497ZM13.5497 2.50529C13.9924 2.00301 14.5578 1.75228 15.2464 1.75228H17.0827C17.8258 1.75228 18.1977 2.13079 18.1977 2.88702C18.1977 3.23419 18.1099 3.48493 17.9356 3.64003C17.7606 3.79514 17.4872 3.87229 17.1159 3.87229H12.9362C13.024 3.32661 13.2287 2.87095 13.5511 2.50529H13.5497Z" fill="#161616" />
                    <path d="M31 9.0598H22.7529C22.2512 9.0598 22 8.81791 22 8.33492V7.87041C22 7.65986 22.0288 7.47984 22.0878 7.33117C22.146 7.18249 22.2807 7.03382 22.4903 6.88515L27.9006 1.93872H22V0.0606079H30.3341C30.7775 0.0606079 30.9992 0.259108 30.9992 0.655304V1.06436C30.9992 1.38662 30.8706 1.65343 30.6141 1.86398L24.8012 7.16401H30.9992V9.06061L31 9.0598Z" fill="#161616" />
                    <path d="M35.5497 6.53154C35.2316 6.17473 35.0275 5.7287 34.9383 5.19267H39.4758C40.35 5.19267 40.9891 5.0006 41.3936 4.61646C41.7981 4.23232 42 3.64968 42 2.86854C42 2.02552 41.7761 1.34724 41.3277 0.832106C40.8793 0.317774 40.2239 0.0606079 39.3604 0.0606079H37.262C36.6605 0.0606079 36.098 0.1691 35.5738 0.386084C35.0488 0.603067 34.5954 0.910059 34.2128 1.30625C33.8303 1.70325 33.5327 2.18062 33.3195 2.73835C33.1063 3.29608 33 3.898 33 4.54172C33 5.18544 33.1063 5.79058 33.3195 6.35474C33.5327 6.9189 33.8303 7.40269 34.2128 7.80532C34.5954 8.20794 35.0488 8.51815 35.5738 8.73513C36.098 8.95212 36.6612 9.06061 37.262 9.06061H41.5735V7.27572H37.2457C36.5571 7.27572 35.9917 7.02819 35.549 6.53154H35.5497ZM35.5497 2.50529C35.9924 2.00301 36.5578 1.75228 37.2464 1.75228H39.0827C39.8258 1.75228 40.1977 2.13079 40.1977 2.88702C40.1977 3.23419 40.1099 3.48493 39.9356 3.64003C39.7606 3.79514 39.4872 3.87229 39.1159 3.87229H34.9362C35.024 3.32661 35.2287 2.87095 35.5504 2.50529H35.5497Z" fill="#161616" />
                    <path d="M54.9992 9.05981H52.7647V4.33764C52.7647 3.5936 52.5532 3.02391 52.1287 2.62699C51.7049 2.23006 51.0889 2.0324 50.2796 2.0324H46.2345V9.06061H44V0.0606079H50.3379C51.8531 0.0606079 53.0086 0.432628 53.8053 1.17587C54.6012 1.91991 55 2.97892 55 4.35531V9.059L54.9992 9.05981Z" fill="#161616" />
                    <path d="M68 9.06061H65.3055L61.9042 5.78737L58.5797 9.06061H56L60.6243 4.39305L56.3064 0.0606079H58.981L62.038 2.98024L65.076 0.0606079H67.6367L63.3187 4.39305L68 9.06061Z" fill="#161616" />
                </svg>

                <p className='font-montserrat font-medium text-[1.4rem] leading-[150%] tracking-[0] align-middle text-[#26493E]'>Cashback</p>
            </div>

            <div
                className='w-[calc(100%_-_4px)] rounded-[1.1rem] mb-[2px] ml-[2px] px-[3rem] py-[1.5rem] bg-cover bg-center bg-no-repeat'
                style={{
                    backgroundImage: `url(${resolvedTheme === 'dark' ? cashBackBgDark : cashBackBgLight})`
                }}
            >
                <div className='flex justify-between'>
                    <div>
                        <p className='font-montserrat font-medium text-[1.4rem] dark:text-[#BBBBBB] text-[#717171] leading-[150%] tracking-[0]'>Available Cashback</p>

                        <h3 className='font-montserrat font-semibold text-[4.2rem] text-[#3EA584] dark:text-[#57E8BA] leading-[150%] tracking-[0.05em] align-middle'>
                            ${formatCurrency(availableBalance)}
                        </h3>

                        <ul>
                            <li className=' font-medium text-[1rem] leading-[18px] dark:text-[#CDCDCD] text-[#BBBBBB] tracking-[0]'>
                                <span className='font-bold'>${formatCurrency(pendingBalance)}</span> pending
                            </li>

                            {nextVestingAccrual && (
                                <li className='font-medium text-[1rem] leading-[18px] dark:text-[#CDCDCD] text-[#BBBBBB] tracking-[0]'>
                                    <span className='font-bold'>${formatCurrency(nextVestingAccrual.cashbackEarned)}</span> available on {formatDate(nextVestingDate)}
                                </li>
                            )}
                        </ul>
                    </div>

                    <div className='relative'>
                        <div className="rounded-[1rem] relative z-[10] h-full p-[0.5px] bg-[linear-gradient(180deg,#57E8BA_0%,#CBDFD9_100%)] dark:bg-[linear-gradient(180deg,#57E8BA_0%,#318268_100%)]">
                            <div className="rounded-[1rem] h-full w-full px-[3rem] py-[1.5rem] overflow-hidden bg-[radial-gradient(228.01%_205.32%_at_54.45%_25.3%,_#F6F9F8_27.15%,_#4BD5A9_94.09%)] dark:bg-[radial-gradient(228.01%_205.32%_at_54.45%_25.3%,_#05110D_27.23%,_#57E8BA_94.09%)]">
                                <div className="w-full h-full flex flex-col justify-center items-center">
                                    <p className='font-semibold text-[3.4rem] text-[#3EA584] dark:text-[#57E8BA] leading-[150%] tracking-[0.03em] text-center align-middle'>
                                        {getTierRange()}
                                    </p>
                                    <p className='font-normal text-[1rem] dark:text-[#D7D7D7] text-[#929292] leading-[150%] tracking-[0] text-center align-middle'>Based on monthly volume</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
